/**
 * @voltxampere/web — `<vxa-canvas>` (Sprint 12 + 13).
 *
 * Lit host for the schematic editor canvas. Owns:
 *   - the world ↔ screen `Viewport`,
 *   - the `DirtyTracker` + `HitTester` indices,
 *   - the active `CanvasTool` (default `SelectTool`; `PanTool`
 *     while Space is held; transient `DragTool` /
 *     `RubberBandTool` for click-and-drag interactions; modal
 *     `StampTool` / `WireTool` / `EraseTool` activated by
 *     keyboard shortcut or sidebar card),
 *   - the Sprint 13 `UndoStack`,
 *   - a private `Scene` signal derived from `currentCircuit` plus
 *     selection / hover state plus per-tool overlay nodes
 *     (ghosts, selection box, erase preview), and
 *   - the `CanvasRenderer` rAF loop.
 *
 * The element keeps keyboard event handling local to the canvas
 * (`<canvas tabindex="0">` + `pointerdown` auto-focuses). Sprint
 * 11's `ShortcutManager` covers the global keyboard surface (R/C/
 * L/D stamp arms, Cmd+K palette, etc.); the canvas handler picks
 * up viewport-local keys (Space, '0', Tab, Esc) plus the live
 * tool's `onKeyDown` forward.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { LitElement, css, html } from "lit";
import { customElement } from "lit/decorators.js";
import { effect, signal, type Signal } from "@preact/signals-core";
import { t } from "../i18n/index.js";

import {
  AddAnnotationCommand,
  AnnotationTool,
  BatchCommand,
  CanvasRenderer,
  DeleteCommand,
  DirtyTracker,
  DragTool,
  EraseTool,
  HitTester,
  PanTool,
  PasteCommand,
  RotateCommand,
  RubberBandTool,
  SelectTool,
  StampTool,
  nextRotation,
  type Command,
  type UndoStack,
  Viewport,
  WireTool,
  ZENIT_THEME,
  copyToClipboard,
  readFromClipboard,
  sceneFromCircuit,
  type CanvasTool,
  type Layer,
  isComponentNode,
  drawWireVibration,
  drawWireFlow,
  drawSolderShimmer,
  type Scene,
  type SceneNode,
  type ToolContext,
} from "@voltxampere/ui/canvas";
import type { Annotation } from "@voltxampere/schema";
import { currentCircuit } from "../store/circuit.js";
import { selectedNodes } from "../store/canvas.js";
// Sprint 57 — polish overlay hover tooltip target setter.
import { setHoverTooltipTarget } from "../store/polish-effects.js";
// Sprint 58 — wire vibration renderer (S57 partial closure) + snap
// glow drag-tool sink (S57 partial closure).
import {
  VIBRATION_AMPLITUDE_PX,
  setSnapGlowTarget,
  wireVibrationStates,
} from "../store/polish-effects.js";
// Sprint 58b — connection animation + solder shimmer + LOD priority.
import { connectionAnimationStates, pruneExpiredFlashes } from "../store/connection-animation.js";
import { activeSparkles, joints, tickShimmer } from "../store/solder-shimmer-state.js";
import { effectEnabled, recordFrame as recordPolishFrame } from "../store/polish-priority.js";
import { armedSymbol } from "../store/ui.js";
// Sprint 25 R4 lane 3 — canvas preference signals are imported here
// so the canvas re-runs its scene-derivation effect whenever the
// user flips a Görünüm ayarı (canvasStyle / wireCurve / symbolStandard).
// The renderer does not yet consume these signals — Lane 4E
// (R3 backlog) ports v1's spice-layout.js and ties wire routing to
// `wireCurve`. Until then the imports keep the reactivity wired so
// the eventual integration only needs to read inside the existing
// effect block, not re-thread the signal graph.
import { canvasStyle, symbolStandard, wireCurve } from "../store/canvas-prefs.js";
import { runDc } from "../store/dc-result.js";
import { runAC as runACFromStore } from "../store/ac-runner.js";
import { undoStack } from "../store/undo-stack.js";
import {
  setCanvasShortcutHandlers,
  type CanvasShortcutHandlers,
} from "../shortcuts/canvas-catalog.js";
import { fragmentFromSelection } from "../canvas/fragment.js";
import { voltageLabels } from "../canvas/voltage-labels.js";
// Sprint 29-5 — fx overlay (current-flow particles + glow).
import { drawFxOverlay, reconcileFromFrame } from "../canvas/fx-overlay.js";
// Sprint 30-3 — voltage heatmap radial overlay.
import { drawVoltageHeatmap } from "../canvas/voltage-heatmap.js";
// Sprint 30-4 — wire color gradient stroke.
import { drawWireGradient } from "../canvas/wire-gradient.js";
// Sprint 31-6 — LED brightness halos (component fx layer).
import { drawLedOverlay } from "../canvas/led-overlay.js";
import {
  currentFxFrame,
  currentVoltageSamples,
  prefersReducedMotion,
  voltageGradientEnabled,
  voltageHeatmapEnabled,
} from "../store/fx-state.js";
import { createMeteorPool, type MeteorPool } from "@voltxampere/fx";
// Sprint 37-3 — damage state restart UX (Sprint 36 carry-over).
import { damagedCount, resetDamage } from "../store/damage-state.js";
import { SignalController } from "../lit-utils/signal-controller.js";

interface PendingAnnotation {
  readonly position: { x: number; y: number };
  text: string;
}

const HIT_BOUNDS = { x: -10000, y: -10000, w: 20000, h: 20000 };

@customElement("vxa-canvas")
export class VxaCanvas extends LitElement {
  static override styles = css`
    :host {
      display: block;
      width: 100%;
      height: 100%;
      background: var(--void);
      outline: none;
      position: relative;
      contain: strict;
    }
    canvas {
      display: block;
      width: 100%;
      height: 100%;
      cursor: var(--vxa-canvas-cursor, default);
      outline: none;
    }
    /* Sprint 29-5 — fx overlay (current flow particles + glow).
     * Sits above the schematic canvas, pointer-events: none so it
     * never intercepts pan/zoom/click. Composited via CSS absolute
     * positioning + matched dimensions (set in JS to mirror the
     * underlying canvas size + DPR). */
    canvas[data-fx] {
      position: absolute;
      inset: 0;
      pointer-events: none;
    }
    /* Sprint 18 Paket C C4 — compass N/S/E/W overlay.
     * Decorative only (pointer-events: none). The four cardinal
     * letters sit a small inset away from each canvas edge in
     * JetBrains Mono 9px / brass opacity 0.8 — the mockup
     * (zenit-platform.html line 1721-1724) uses these as a
     * subtle "you are looking at a workshop bench" cue. */
    .compass {
      position: absolute;
      inset: 0;
      pointer-events: none;
      font-family: "JetBrains Mono", monospace;
      font-size: 9px;
      color: var(--brass);
      opacity: 0.8;
      letter-spacing: 0.18em;
    }
    .compass span {
      position: absolute;
      transform: translate(-50%, -50%);
    }
    .compass span[data-dir="N"] {
      top: 8px;
      left: 50%;
    }
    .compass span[data-dir="S"] {
      bottom: 8px;
      top: auto;
      left: 50%;
      transform: translate(-50%, 50%);
    }
    .compass span[data-dir="W"] {
      left: 12px;
      top: 50%;
    }
    .compass span[data-dir="E"] {
      right: 12px;
      left: auto;
      top: 50%;
      transform: translate(50%, -50%);
    }
    /* Sprint 37-3 — restart button (Devreyi Sıfırla). Conditional
     * render guarded by damagedCount > 0 in render(). Brass-edged
     * void surface, top-right, brass badge with damaged count. */
    .vxa-canvas-restart-btn {
      position: absolute;
      top: 16px;
      right: 16px;
      background: var(--void);
      color: var(--ivory);
      border: 1px solid var(--brass);
      padding: 8px 16px;
      font-family: "EB Garamond", serif;
      font-style: italic;
      font-size: 14px;
      cursor: pointer;
      transition:
        background 0.2s ease,
        border-color 0.2s ease;
      display: flex;
      align-items: center;
      gap: 8px;
      z-index: 5;
    }
    .vxa-canvas-restart-btn:hover {
      background: var(--ink2);
      border-color: var(--brass);
    }
    .vxa-canvas-restart-btn:focus-visible {
      outline: 2px solid var(--brass);
      outline-offset: 2px;
    }
    .vxa-canvas-restart-btn .badge {
      background: var(--brass);
      color: var(--void);
      padding: 2px 8px;
      font-style: normal;
      font-weight: 600;
      font-family: "JetBrains Mono", monospace;
      font-size: 11px;
      letter-spacing: 0.05em;
      border-radius: 2px;
    }
  `;

  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private renderer: CanvasRenderer | null = null;
  // Sprint 29-5 — fx overlay canvas (current-flow particles + wire
  // glow). Sits in front of the schematic canvas, repaints on every
  // fxFrameSignal change. The fx renderer is local to apps/web (no
  // change to @voltxampere/ui's CanvasRenderer) so the schematic
  // pipeline stays unchanged.
  private fxCanvas: HTMLCanvasElement | null = null;
  private fxCtx: CanvasRenderingContext2D | null = null;
  private readonly meteorPool: MeteorPool = createMeteorPool();
  // Sprint 37-3 — re-render restart button when damaged-count changes.
  private readonly _damagedCount = new SignalController(this, damagedCount);
  private fxRafId: number | null = null;
  private fxLastFrameTime = 0;
  // Sprint 58 — shared phase for wire vibration overlay so the
  // ripple is coherent across all high-current wires.
  private vibrationPhase = 0;
  // Sprint 58b — shared dash offset for the marching-ants connection
  // animation. Advances per rAF frame at a constant rate so all
  // active wires march in sync.
  private connectionDashOffset = 0;
  /** Boot-only auto-fit guard — Sprint 19 hotfix-2. Set to true the
   *  first time the canvas paints, so subsequent resizes (DevTools
   *  toggle, window drag) keep the user's pan/zoom choices instead
   *  of snapping back to fit. Sprint 25 R5-CR-1 (F-V-01) reset's
   *  this whenever `currentCircuit.id` changes so a newly-loaded
   *  circuit re-fits exactly once — keeping the user's pan/zoom
   *  on the SAME circuit while restoring fit when the circuit
   *  identity is genuinely new. */
  private hasAutoFit = false;
  /** Sprint 25 R5-CR-1 (F-V-01) — last circuit id that triggered
   *  auto-fit. The auto-fit effect compares against this to detect
   *  identity changes; same-id signal updates (undo on the same
   *  circuit, IDB hydrate of identical id, etc.) skip re-fit so
   *  user pan/zoom is preserved. */
  private lastAutoFitCircuitId: string | null = null;
  private readonly viewport = new Viewport();
  private readonly dirty = new DirtyTracker();
  private readonly hitTester = new HitTester(HIT_BOUNDS);
  private readonly hoveredNode: Signal<SceneNode | null> = signal(null);
  // Sprint 14 — selection lives in the store so the inspector
  // can read it without reaching into this element.
  private readonly selectedNodes: Signal<readonly string[]> = selectedNodes;
  private readonly sceneSig: Signal<Scene> = signal(
    sceneFromCircuit({ components: [], wires: [] }, this.viewport.snapshot(), ZENIT_THEME),
  );
  private currentTool: CanvasTool = new SelectTool();
  private readonly panTool = new PanTool();
  private readonly dragTool = new DragTool();
  private readonly rubberBandTool = new RubberBandTool({
    onExit: () => {
      this.restorePreviousTool();
    },
  });
  private readonly stampTool = new StampTool({
    onExit: () => {
      armedSymbol.value = null;
      this.restorePreviousTool();
    },
  });
  private readonly wireTool = new WireTool();
  private readonly eraseTool = new EraseTool({
    onExit: () => {
      this.restorePreviousTool();
    },
  });
  private readonly annotationTool = new AnnotationTool({
    onRequestEdit: (position) => {
      this.openAnnotationInput(position);
    },
    onExit: () => {
      this.restorePreviousTool();
    },
  });
  private readonly toolStack: CanvasTool[] = [];
  // Sprint 14.0.1 — undo stack lifted into the store so the
  // inspector + canvas tools share the same history (D6 closure).
  private readonly undoStack = undoStack;
  private resizeObserver: ResizeObserver | null = null;
  private lastPointerScreen: { x: number; y: number } | null = null;
  private pendingAnnotation: PendingAnnotation | null = null;
  private readonly disposers: (() => void)[] = [];

  override firstUpdated(): void {
    const root = this.renderRoot as ShadowRoot;
    const c = root.querySelector("canvas");
    if (!c) {
      console.warn("[vxa-canvas] <canvas> missing from shadow root, skipping setup");
      return;
    }
    this.canvas = c;
    const ctx = this.canvas.getContext("2d");
    if (!ctx) {
      console.warn("[vxa-canvas] 2D context unavailable — canvas renderer disabled");
      return;
    }
    this.ctx = ctx;

    // Sprint 29-5 — pick up the fx overlay canvas (sibling of the
    // schematic canvas) and bind a per-frame redraw effect to the
    // fxFrameSignal. Failure to grab the fx context is non-fatal —
    // animation just stays off and the schematic canvas paints alone.
    const fxC = root.querySelector<HTMLCanvasElement>("canvas[data-fx]");
    if (fxC) {
      const fxCtx2d = fxC.getContext("2d");
      if (fxCtx2d) {
        this.fxCanvas = fxC;
        this.fxCtx = fxCtx2d;
        this.setupFxOverlayEffect();
      }
    }

    // Scene derivation effect — circuit + viewport + selection +
    // hover state + active-tool overlays all rebuild the immutable
    // Scene snapshot.
    this.disposers.push(
      effect(() => {
        // Sprint 25 R4 lane 3 wired the dirty bit + reactivity for
        // every canvas-pref signal so this effect re-runs on toggle.
        // Sprint 52 ADR-0085 cashes that wiring in: `wireCurve` is now
        // threaded into `sceneFromCircuit` so the renderer dispatches
        // catenary / manhattan / linear per the user's setting.
        // `canvasStyle` and `symbolStandard` keep the read for the same
        // reactivity contract; their downstream integration lands in a
        // later sprint (Lane 4E successor).
        void canvasStyle.value;
        void symbolStandard.value;
        const circuit = currentCircuit.value;
        const scene = sceneFromCircuit(
          circuit ?? { components: [], wires: [] },
          {
            center: this.viewport.center.value,
            zoom: this.viewport.zoom.value,
            rotation: this.viewport.rotation.value,
          },
          ZENIT_THEME,
          {
            selectedIds: this.selectedNodes.value,
            hoveredId: this.hoveredNode.value?.id ?? null,
            wireCurve: wireCurve.value,
          },
        );
        const overlay = this.collectOverlayNodes();
        const layered = injectOverlay(scene, overlay);
        this.sceneSig.value = layered;
        this.hitTester.rebuild(layered.layers);
        this.dirty.markAllDirty();
      }),
    );

    // Sprint 25 R5-CR-1 (F-V-01) — auto-fit on circuit identity
    // change. The Sprint 19 hotfix-2 first-mount auto-fit (see
    // applyResize) is brittle against the boot-time race: vxa-canvas
    // mounts before main.ts's top-level `await pickBootCircuit()`
    // resolves, so the first applyResize sees `currentCircuit.value
    // === null`, skips the auto-fit branch, and `hasAutoFit` stays
    // false; but ResizeObserver doesn't fire again because the
    // canvas size didn't change. Result on the boot fixture
    // (mockup-canonical world coords 200-440): R₁ + R₂ + the right
    // half of both wires sit ~20 px past the right edge of the
    // default viewport (centre 0,0 zoom 1) and never paint.
    //
    // This effect closes the gap by listening to circuit identity:
    // every distinct `circuit.id` resets the auto-fit guard and
    // re-runs applyResize, so boot / preset / IDB hydrate / share
    // hash all land with the circuit centred and zoomed in. Pan +
    // zoom on the SAME circuit (same id) are preserved because the
    // identity comparison short-circuits.
    //
    // v1 parity: `~/Desktop/VoltXAmpere/src/ui/ui-extras.js`'s
    // `fitToScreen()` is called explicitly from every load path
    // (tutorials, spice-import, auto-wire, manual menu, Ctrl+Shift+
    // F). v3 had the auto-fit logic but no per-load trigger — this
    // effect supplies the trigger. Sprint 26 Pre-Beta UX Pass picks
    // up the larger refactor (shared `fitToScreen()` utility +
    // explicit calls + Ctrl+Shift+F shortcut) per Class C carry-
    // over noted in `tools/triage/reports/TRIAGE_R5CR1_BOOT_PAINT
    // .md`.
    this.disposers.push(
      effect(() => {
        const c = currentCircuit.value;
        const newId = c?.id ?? null;
        if (newId === null || newId === this.lastAutoFitCircuitId) return;
        this.lastAutoFitCircuitId = newId;
        this.hasAutoFit = false;
        this.applyResize();
      }),
    );

    // armedSymbol → StampTool activation (Sprint 11 catalog sets
    // the signal; Sprint 13 reacts).
    this.disposers.push(
      effect(() => {
        const symbol = armedSymbol.value;
        if (symbol) {
          // Sprint 38-1 — variant entries override the canonical
          // engine type via `schemaType` (e.g. "2n3904" symbol →
          // "bjt-npn" type) and pre-fill instance.params via
          // `defaultParams` (datasheet Is/Bf/Vaf). Falls back to
          // legacy Sprint 13 behavior (symbol.id as type, empty
          // params) when both fields are absent.
          this.stampTool.setTemplate({
            type: symbol.schemaType ?? symbol.id,
            ...(symbol.defaultLabel !== undefined ? { defaultLabel: symbol.defaultLabel } : {}),
            ...(symbol.defaultParams !== undefined ? { params: symbol.defaultParams } : {}),
          });
          if (this.currentTool.id !== "stamp") {
            this.toolStack.push(this.currentTool);
            this.swapTool(this.stampTool);
          }
        }
      }),
    );

    this.renderer = new CanvasRenderer(this.ctx, this.sceneSig, this.dirty, this.canvas);
    this.disposers.push(this.renderer.start());

    // applyResize sets canvas pixel dims AND triggers boot auto-fit
    // (the previous Sprint 19 hotfix only adjusted the centre, but
    // when the canvas pane is shorter than the circuit bounds, the
    // top + bottom components — V₁ at y=140 and GND at y=440 — fell
    // outside the viewport even with the centre nudged. Computing
    // zoom relative to actual canvas dims keeps the whole circuit
    // on-screen at every common pane height).
    this.applyResize();
    if (typeof ResizeObserver !== "undefined") {
      this.resizeObserver = new ResizeObserver(() => {
        this.applyResize();
      });
      this.resizeObserver.observe(this);
    }

    this.attachEvents();
    this.style.setProperty("--vxa-canvas-cursor", this.currentTool.cursor);
    this.currentTool.onEnter?.(this.toolContext());

    // Hand the canvas-catalog its dispatchers.
    setCanvasShortcutHandlers(this.shortcutHandlers());
    this.disposers.push(() => {
      setCanvasShortcutHandlers(null);
    });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    for (const d of this.disposers.splice(0)) d();
    this.renderer?.dispose();
    this.renderer = null;
  }

  override render(): unknown {
    const overlay = this.pendingAnnotation;
    const overlayTpl = overlay ? this.renderAnnotationOverlay(overlay) : null;
    // Sprint 18 Paket C C4 — compass N/S/E/W cue.
    const compass = html`<div class="compass" data-testid="canvas-compass">
      <span data-dir="N">N</span>
      <span data-dir="S">S</span>
      <span data-dir="E">E</span>
      <span data-dir="W">W</span>
    </div>`;
    // Sprint 37-3 — restart button (Devreyi Sıfırla). Conditional
    // render — only when at least one component has been damaged.
    const damagedN = this._damagedCount.value;
    const restartBtn =
      damagedN > 0
        ? html`<button
            type="button"
            class="vxa-canvas-restart-btn"
            data-testid="canvas-restart-btn"
            aria-label=${t("canvas.restart.aria")}
            @click=${this.handleRestart}
          >
            <span>${t("canvas.restart.label")}</span>
            <span class="badge" data-testid="canvas-restart-badge">${damagedN}</span>
          </button>`
        : null;
    return [
      html`<canvas tabindex="0" role="application" aria-label=${t("canvas.aria.label")}></canvas>`,
      // Sprint 29-5 — fx overlay (current-flow particles + glow).
      // Drawn AFTER the schematic canvas so particles + glow paint
      // on top. pointer-events: none keeps interaction on the base
      // canvas.
      html`<canvas data-fx aria-hidden="true"></canvas>`,
      compass,
      restartBtn,
      overlayTpl,
    ];
  }

  /**
   * Sprint 37-3 — Devreyi Sıfırla handler. Wipes the damage map so
   * every component returns to healthy state, then re-runs the
   * transient solve so the canvas reflects the restored circuit.
   *
   * The audio dispatcher (Sprint 37-2) silences sustained loops
   * automatically when components disappear from the damage map.
   *
   * Re-run uses the same dynamic-import lane as `runActiveSolve` so
   * the bundle code-splits the transient runner consistently.
   */
  private handleRestart = (): void => {
    resetDamage();
    const circuit = currentCircuit.value;
    if (circuit === null) return;
    void import("../store/transient-runner.js").then(({ runTransient }) => {
      void runTransient(circuit);
    });
  };

  private renderAnnotationOverlay(pending: PendingAnnotation): unknown {
    // Position the input over the snapped world point. Conversion
    // happens at render time so pan/zoom keep it pinned.
    // `this.canvas` is non-null inside render() once firstUpdated()
    // has run; the assertion is purely a type guard.
    const screen = this.viewport.worldToScreen(pending.position, this.canvas);
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Enter") {
        e.preventDefault();
        this.commitAnnotation((e.target as HTMLInputElement).value);
      } else if (e.key === "Escape") {
        e.preventDefault();
        this.cancelAnnotation();
      }
    };
    return html`<input
      class="annotation-input"
      type="text"
      data-input="annotation"
      autofocus
      style=${`position:absolute;left:${String(screen.x)}px;top:${String(screen.y)}px;` +
      `font:12px 'EB Garamond',serif;background:var(--ink2);color:var(--ivory);` +
      `border:1px solid var(--brass);padding:2px 4px;`}
      .value=${pending.text}
      @keydown=${onKey}
    />`;
  }

  // ── public for tests ─────────────────────────────────────────────

  get activeToolId(): string {
    return this.currentTool.id;
  }

  get selectionIds(): readonly string[] {
    return this.selectedNodes.value;
  }

  get undoStackInstance(): UndoStack {
    return this.undoStack;
  }

  /** Test helper — synthesise the catalog's handler table. */
  shortcutHandlers(): CanvasShortcutHandlers {
    return {
      activateWireTool: () => {
        this.activateModalTool(this.wireTool);
      },
      activateEraseTool: () => {
        this.activateModalTool(this.eraseTool);
      },
      activateAnnotationTool: () => {
        this.activateModalTool(this.annotationTool);
      },
      undo: () => {
        this.runUndo();
      },
      redo: () => {
        this.runRedo();
      },
      copy: () => {
        void this.runCopy();
      },
      paste: () => {
        void this.runPaste();
      },
      cut: () => {
        void this.runCut();
      },
      deleteSelected: () => {
        this.deleteSelected();
      },
      rotateSelected: () => {
        this.rotateSelected();
      },
      runDc: () => {
        this.requestDcRun();
      },
      runTransient: () => {
        this.requestTransientRun();
      },
      toggleLive: () => {
        void this.requestToggleLive();
      },
      toggleWaveform: () => {
        void this.requestToggleWaveform();
      },
      runAC: () => {
        this.requestACRun();
      },
    };
  }

  private requestACRun(): void {
    // Sprint 25 R5 R5-CR-4 — eliminate dynamic-import cold-start.
    // `ac-runner.js` is statically imported by 4 other modules
    // (vxa-ac-config-dialog, vxa-bode-plot, vxa-layout, main) so it
    // already lives in the main bundle chunk; keeping the dynamic
    // `import("../store/ac-runner.js")` here only added a Promise
    // microtask + Vite warning (chunk-split impossible) without
    // any code-split benefit.
    const circuit = currentCircuit.value;
    if (!circuit) return;
    void runACFromStore(circuit);
  }

  private requestTransientRun(): void {
    const circuit = currentCircuit.value;
    if (!circuit) return;
    void import("../store/transient-runner.js").then(({ runTransient }) => {
      void runTransient(circuit);
    });
  }

  private async requestToggleLive(): Promise<void> {
    const { transientLive, startLive, stopLive } = await import("../store/transient-runner.js");
    if (transientLive.value) {
      stopLive();
    } else {
      startLive(() => currentCircuit.value);
    }
  }

  private async requestToggleWaveform(): Promise<void> {
    const { waveformExpanded } = await import("./vxa-waveform.js");
    waveformExpanded.value = !waveformExpanded.value;
  }

  // ── internals ────────────────────────────────────────────────────

  private collectOverlayNodes(): SceneNode[] {
    const out: SceneNode[] = [];
    if (this.currentTool === this.stampTool) out.push(...this.stampTool.ghostNodes());
    if (this.currentTool === this.wireTool) out.push(...this.wireTool.ghostNodes());
    if (this.currentTool === this.eraseTool) out.push(...this.eraseTool.previewNodes());
    if (this.currentTool === this.rubberBandTool) out.push(...this.rubberBandTool.previewNodes());
    if (this.currentTool === this.annotationTool) out.push(...this.annotationTool.ghostNodes());
    // Sprint 18 D1 — live voltage labels (probe-overlay nodes
    // derived from circuit + currentDCResult). Emitted into the
    // selection layer via `injectOverlay` so they ride on top of
    // wires/components and stay visible during pan/zoom.
    out.push(...voltageLabels.value);
    return out;
  }

  private requestDcRun(): void {
    const circuit = currentCircuit.value;
    if (!circuit) return;
    void runDc(circuit);
  }

  private openAnnotationInput(position: { x: number; y: number }): void {
    this.pendingAnnotation = { position, text: "" };
    this.requestUpdate();
  }

  private commitAnnotation(text: string): void {
    if (!this.pendingAnnotation) return;
    const trimmed = text.trim();
    const position = this.pendingAnnotation.position;
    this.pendingAnnotation = null;
    if (trimmed.length === 0) {
      this.requestUpdate();
      return;
    }
    const circuit = currentCircuit.value;
    if (!circuit) {
      this.requestUpdate();
      return;
    }
    const annotation: Annotation = {
      id: crypto.randomUUID(),
      kind: "text",
      position,
      content: trimmed,
    };
    currentCircuit.value = this.undoStack.execute(new AddAnnotationCommand(annotation), circuit);
    this.requestUpdate();
  }

  private cancelAnnotation(): void {
    this.pendingAnnotation = null;
    this.requestUpdate();
  }

  /**
   * Sprint 29-5 — wire the fx overlay redraw loop. The fxFrameSignal
   * encodes the user's three-layer reduced-motion guard (AYARLAR
   * toggle + matchMedia + DC mode skip — see fx-state.ts), so when
   * the frame is empty (zero wires) the rAF loop's draw call paints
   * nothing and the canvas stays cleared.
   *
   * The rAF loop runs continuously while there are particles to step;
   * when the frame is empty we still tick once to clear the canvas
   * and then idle until the signal flips.
   */
  private setupFxOverlayEffect(): void {
    const tick = (time: number): void => {
      this.fxRafId = null;
      const fxCtx = this.fxCtx;
      const fxCanvas = this.fxCanvas;
      if (!fxCtx || !fxCanvas) return;
      const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
      const cssWidth = Math.max(1, Math.floor(fxCanvas.clientWidth || 1));
      const cssHeight = Math.max(1, Math.floor(fxCanvas.clientHeight || 1));
      const dt =
        this.fxLastFrameTime > 0 ? Math.min(0.05, (time - this.fxLastFrameTime) / 1000) : 0;
      this.fxLastFrameTime = time;

      const frame = currentFxFrame.value;
      const reducedMotion = prefersReducedMotion.value;
      const heatmapOn = voltageHeatmapEnabled.value;
      const gradientOn = voltageGradientEnabled.value;
      const samples = currentVoltageSamples.value;

      reconcileFromFrame(this.meteorPool, frame);

      const circuit = currentCircuit.value;
      const wireList = circuit ? circuit.wires : [];
      const wires = new Map(wireList.map((w) => [w.id, w]));
      const viewport = {
        center: this.viewport.center.value,
        zoom: this.viewport.zoom.value,
      };

      // Sprint 30-3 — single clear pass per frame (moved from fx-overlay
      // to here so heatmap + wire-gradient + particles share one canvas
      // without overwriting each other). Identity transform for
      // clearRect; per-layer drawers reset their own world transform.
      fxCtx.save();
      fxCtx.setTransform(dpr, 0, 0, dpr, 0, 0);
      fxCtx.clearRect(0, 0, cssWidth, cssHeight);
      fxCtx.restore();

      // Layer 1: voltage heatmap (Sprint 30-3) — STATIC overlay, drawn
      // even when reduced-motion is on (it's a visual cue, not motion).
      if (heatmapOn && samples.length > 0) {
        drawVoltageHeatmap(fxCtx, {
          viewport,
          cssWidth,
          cssHeight,
          dpr,
          samples,
        });
      }

      // Layer 2: wire color gradient strokes (Sprint 30-4) — also
      // static; reads net voltage from samples and tints each wire
      // through the vfLow→vfHigh palette via HSL space.
      if (gradientOn && samples.length > 0 && wireList.length > 0) {
        drawWireGradient(fxCtx, {
          viewport,
          cssWidth,
          cssHeight,
          dpr,
          wires: wireList,
          samples,
        });
      }

      // Layer 3: meteor particles + wire glow (Sprint 29-5).
      drawFxOverlay(fxCtx, this.meteorPool, frame, dt, reducedMotion, {
        viewport,
        cssWidth,
        cssHeight,
        dpr,
        wires,
      });

      // Layer 5: Sprint 58 — high-current wire vibration overlay
      // (S57 partial closure). Iterate the polish-effects store
      // `wireVibrationStates` snapshot, transform each path to
      // screen coords via the same Viewport mapping the static
      // schematic uses, and stamp a sinüsoid-perturbed copy onto
      // the fx canvas. Phase is shared across all wires for a
      // coherent "buzz" rather than per-wire chaos. Reduced motion
      // short-circuits the phase update + draw call entirely.
      const vibStates = wireVibrationStates.value;
      const vibEnabled = effectEnabled.value.vibration;
      if (vibEnabled && vibStates.length > 0 && !reducedMotion && wireList.length > 0) {
        this.vibrationPhase += dt * (2 * Math.PI * 6);
        const wireMap = new Map<string, (typeof wireList)[number]>();
        for (const w of wireList) wireMap.set(w.id, w);
        for (const v of vibStates) {
          const wire = wireMap.get(v.wireId);
          if (!wire || wire.path.length < 2) continue;
          const screenPath = wire.path.map((p) => this.viewport.worldToScreen(p, fxCanvas));
          drawWireVibration(
            fxCtx,
            {
              path: screenPath,
              amplitudePx: v.amplitude * VIBRATION_AMPLITUDE_PX,
              phase: this.vibrationPhase,
            },
            ZENIT_THEME,
          );
        }
      }

      // Layer 4: LED brightness halos (Sprint 31-6) — drawn LAST so
      // emission overrides the colder heatmap blue underneath at the
      // LED position. Brightness 0 LEDs skip painting (no-op).
      if (circuit) {
        const leds = new Map<string, (typeof circuit.components)[number]>();
        for (const c of circuit.components) {
          if (c.type === "led") leds.set(c.id, c);
        }
        if (leds.size > 0) {
          drawLedOverlay(fxCtx, {
            viewport,
            cssWidth,
            cssHeight,
            dpr,
            leds,
            fxStates: frame.components,
          });
        }
      }

      // Re-schedule only when there is something to animate (particles)
      // OR a static heatmap that needs to stay painted across frames.
      // The heatmap is static so we only need the rAF loop while
      // particles are stepping; idle DC mode with heatmap visible
      // settles after one tick (no further rAF needed).
      // Sprint 58b — Layer 6: connection animation (marching ants +
      // post-commit flash). Reads connectionAnimationStates signal +
      // gates on effectEnabled["connection-animation"].
      const connEnabled = effectEnabled.value["connection-animation"];
      const connStates = connectionAnimationStates.value;
      if (connEnabled && connStates.length > 0 && !reducedMotion) {
        this.connectionDashOffset += dt * 30; // ~30 px/sec march
        const nowMs = performance.now();
        pruneExpiredFlashes(nowMs);
        for (const state of connStates) {
          const screenPath = state.path.map((p) => this.viewport.worldToScreen(p, fxCanvas));
          drawWireFlow(
            fxCtx,
            {
              path: screenPath,
              mode: state.mode,
              dashOffset: this.connectionDashOffset,
              ageMs: nowMs - state.startedAtMs,
            },
            ZENIT_THEME,
          );
        }
      }

      // Sprint 58b — Layer 7: solder bond shimmer. tickShimmer
      // advances per-joint scheduling + emits new sparkles within
      // the concurrent cap. Then we render the metallic base for
      // every visible joint and overlay sparkle for the active ones.
      const shimmerEnabled = effectEnabled.value.shimmer;
      if (shimmerEnabled && !reducedMotion) {
        const nowMs = performance.now();
        tickShimmer(nowMs);
        const jointList = joints.value;
        const sparkles = activeSparkles.value;
        const sparkleByKey = new Map<string, (typeof sparkles)[number]>();
        for (const s of sparkles) sparkleByKey.set(s.jointKey, s);
        // Naive viewport cull — skip joints whose mapped screen point
        // is outside the canvas client rect. Quadtree-backed cull is
        // a Sprint 59+ optimisation; on the heavy-smoke fixtures the
        // joint count stays well under the cost ceiling.
        for (const j of jointList) {
          const sp = this.viewport.worldToScreen(j.worldPoint, fxCanvas);
          if (sp.x < -20 || sp.y < -20 || sp.x > cssWidth + 20 || sp.y > cssHeight + 20) {
            continue;
          }
          const key = `${j.componentId}::${j.terminalId}`;
          const sparkleEntry = sparkleByKey.get(key);
          const progress =
            sparkleEntry !== undefined ? (nowMs - sparkleEntry.startedAtMs) / 320 : 0;
          drawSolderShimmer(
            fxCtx,
            {
              point: sp,
              sparkle: sparkleEntry !== undefined,
              sparkleProgress: progress,
            },
            ZENIT_THEME,
          );
        }
      }

      // Sprint 58b — record this frame's dt against the polish FPS
      // monitor so the LOD framework can engage / release tiers.
      recordPolishFrame(dt * 1000);

      // Sprint 58 — re-schedule rAF when wire vibration is active
      // even if meteor pool is empty (vibration runs without
      // transient particles).
      const needsAnim =
        (this.meteorPool.perWire.size > 0 ||
          wireVibrationStates.value.length > 0 ||
          connectionAnimationStates.value.length > 0 ||
          activeSparkles.value.length > 0) &&
        !reducedMotion;
      if (needsAnim) {
        this.fxRafId = window.requestAnimationFrame(tick);
      }
    };

    // Effect: kick the rAF loop whenever the fxFrameSignal changes
    // (e.g. F5 finishes a transient run, or AYARLAR toggle flips).
    this.disposers.push(
      effect(() => {
        // Read every signal so the effect re-runs on any change.
        void currentFxFrame.value;
        void prefersReducedMotion.value;
        // Sprint 30-3 — re-tick when heatmap toggles or samples change.
        void voltageHeatmapEnabled.value;
        void currentVoltageSamples.value;
        // Sprint 30-4 — re-tick when wire gradient toggle flips.
        void voltageGradientEnabled.value;
        if (this.fxRafId === null) {
          this.fxLastFrameTime = 0;
          this.fxRafId = window.requestAnimationFrame(tick);
        }
      }),
    );

    this.disposers.push(() => {
      if (this.fxRafId !== null) {
        window.cancelAnimationFrame(this.fxRafId);
        this.fxRafId = null;
      }
    });
  }

  private applyResize(): void {
    const rect = this.getBoundingClientRect();
    const dpr = typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
    const cssWidth = Math.max(1, Math.floor(rect.width));
    const cssHeight = Math.max(1, Math.floor(rect.height));
    this.canvas.width = Math.floor(cssWidth * dpr);
    this.canvas.height = Math.floor(cssHeight * dpr);
    this.canvas.style.width = `${String(cssWidth)}px`;
    this.canvas.style.height = `${String(cssHeight)}px`;
    this.renderer?.setDPR(dpr);
    // Sprint 29-5 — keep the fx overlay canvas in lockstep with the
    // schematic canvas. Same pixel + CSS dims so the per-frame fx
    // draw reads matching coordinates.
    if (this.fxCanvas) {
      this.fxCanvas.width = Math.floor(cssWidth * dpr);
      this.fxCanvas.height = Math.floor(cssHeight * dpr);
      this.fxCanvas.style.width = `${String(cssWidth)}px`;
      this.fxCanvas.style.height = `${String(cssHeight)}px`;
    }

    // Sprint 19 hotfix-2 — boot auto-fit. When the canvas first
    // mounts with non-zero dimensions, fit the viewport to the
    // circuit's component-anchor bounding box (centre + zoom) so
    // every component is visible at any reasonable pane size. The
    // `hasAutoFit` flag stops re-fitting on subsequent resizes —
    // the user's pan/zoom choices stick.
    if (!this.hasAutoFit && cssWidth > 1 && cssHeight > 1) {
      const circuit = currentCircuit.value;
      if (circuit && circuit.components.length > 0) {
        let minX = Infinity;
        let maxX = -Infinity;
        let minY = Infinity;
        let maxY = -Infinity;
        for (const cmp of circuit.components) {
          if (cmp.position.x < minX) minX = cmp.position.x;
          if (cmp.position.x > maxX) maxX = cmp.position.x;
          if (cmp.position.y < minY) minY = cmp.position.y;
          if (cmp.position.y > maxY) maxY = cmp.position.y;
        }
        if (Number.isFinite(minX) && Number.isFinite(minY)) {
          // Pad the bounds so symbols at the edges don't kiss the
          // canvas border; the symbol viewBox is 40×26, so a
          // half-symbol margin avoids visual clipping plus leaves
          // breathing room for the upcoming label/measurement
          // overlays.
          const SYMBOL_MARGIN = 60;
          const boundsW = maxX - minX + SYMBOL_MARGIN * 2;
          const boundsH = maxY - minY + SYMBOL_MARGIN * 2;
          const zoomX = cssWidth / Math.max(boundsW, 1);
          const zoomY = cssHeight / Math.max(boundsH, 1);
          // Clamp to a sensible range so a tiny pane doesn't
          // shrink the symbols below readability and a huge pane
          // doesn't blow them past the renderer's stroke-width
          // budget.
          const fitZoom = Math.max(0.4, Math.min(zoomX, zoomY, 1.5));
          this.viewport.center.value = {
            x: (minX + maxX) / 2,
            y: (minY + maxY) / 2,
          };
          this.viewport.zoom.value = fitZoom;
          this.hasAutoFit = true;
        }
      }
    }
  }

  private attachEvents(): void {
    const c = this.canvas;
    const onPointerDown = (e: PointerEvent): void => {
      c.focus();
      this.lastPointerScreen = { x: e.clientX, y: e.clientY };
      this.routePointerDown(e);
    };
    const onPointerMove = (e: PointerEvent): void => {
      this.lastPointerScreen = { x: e.clientX, y: e.clientY };
      this.currentTool.onPointerMove?.(e, this.toolContext());
      // Sprint 57 — surface the hovered component to the polish
      // overlay so it can render a tooltip. The select tool updated
      // `hoveredNode` synchronously inside its onPointerMove handler.
      const hit = this.hoveredNode.value;
      if (hit !== null && isComponentNode(hit)) {
        setHoverTooltipTarget({
          componentId: hit.id,
          clientX: e.clientX,
          clientY: e.clientY,
        });
      } else {
        setHoverTooltipTarget(null);
      }
    };
    const onPointerUp = (e: PointerEvent): void => {
      this.currentTool.onPointerUp?.(e, this.toolContext());
      this.maybeRestoreAfterTransient();
    };
    const onWheel = (e: WheelEvent): void => {
      e.preventDefault();
      const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
      this.viewport.zoomAt({ x: e.offsetX, y: e.offsetY }, factor, c);
      this.dirty.markAllDirty();
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === " " && this.currentTool.id !== "pan") {
        this.toolStack.push(this.currentTool);
        this.swapTool(this.panTool);
        e.preventDefault();
        return;
      }
      if (e.key === "0" && !e.metaKey && !e.ctrlKey) {
        this.viewport.reset();
        this.dirty.markAllDirty();
        return;
      }
      this.currentTool.onKeyDown?.(e, this.toolContext());
    };
    const onKeyUp = (e: KeyboardEvent): void => {
      if (e.key === " " && this.currentTool.id === "pan" && this.toolStack.length > 0) {
        const prev = this.toolStack.pop();
        if (prev !== undefined) this.swapTool(prev);
        return;
      }
      this.currentTool.onKeyUp?.(e, this.toolContext());
    };

    // Sprint 15 — right-click on a wire opens the probe binding
    // submenu. Hit-test resolves the world point; if it lands on a
    // wire the menu offers CH1-CH4 binding to that wire's netId.
    const onContextMenu = (e: MouseEvent): void => {
      e.preventDefault();
      const ctx = this.toolContext();
      // Hit-test in world coordinates — synthesise a PointerEvent-
      // shaped object so `worldFromEvent` accepts the click.
      const world = ctx.worldFromEvent(e);
      const hit = ctx.hitTester.hit(world);
      if (hit?.kind !== "wire") return;
      const netId = hit.wire.netId;
      void import("../canvas/probe-context-menu.js").then(({ showProbeMenu }) => {
        showProbeMenu({ x: e.clientX, y: e.clientY, netId });
      });
    };

    c.addEventListener("pointerdown", onPointerDown);
    c.addEventListener("pointermove", onPointerMove);
    c.addEventListener("pointerup", onPointerUp);
    c.addEventListener("wheel", onWheel, { passive: false });
    c.addEventListener("keydown", onKeyDown);
    c.addEventListener("keyup", onKeyUp);
    c.addEventListener("contextmenu", onContextMenu);

    this.disposers.push(() => {
      c.removeEventListener("pointerdown", onPointerDown);
      c.removeEventListener("pointermove", onPointerMove);
      c.removeEventListener("pointerup", onPointerUp);
      c.removeEventListener("wheel", onWheel);
      c.removeEventListener("keydown", onKeyDown);
      c.removeEventListener("keyup", onKeyUp);
      c.removeEventListener("contextmenu", onContextMenu);
    });
  }

  /**
   * Sprint 13 — pointerdown router. SelectTool stays the default
   * tool; transient DragTool / RubberBandTool kick in for
   * click-and-drag intent. Modal tools (stamp / wire / erase)
   * receive the event directly.
   */
  private routePointerDown(e: PointerEvent): void {
    if (this.currentTool.id === "select") {
      const ctx = this.toolContext();
      const world = ctx.worldFromEvent(e);
      const hit = ctx.hitTester.hit(world);
      if (hit?.kind === "component" && e.button === 0) {
        this.toolStack.push(this.currentTool);
        this.swapTool(this.dragTool);
      } else if (!hit && e.button === 0) {
        this.toolStack.push(this.currentTool);
        this.swapTool(this.rubberBandTool);
      }
    }
    this.currentTool.onPointerDown?.(e, this.toolContext());
  }

  private maybeRestoreAfterTransient(): void {
    if (this.currentTool === this.dragTool && !this.dragTool.isDragging) {
      this.restorePreviousTool();
    }
  }

  private swapTool(next: CanvasTool): void {
    const ctx = this.toolContext();
    this.currentTool.onLeave?.(ctx);
    this.currentTool = next;
    this.style.setProperty("--vxa-canvas-cursor", next.cursor);
    next.onEnter?.(ctx);
    this.dirty.markAllDirty();
  }

  private restorePreviousTool(): void {
    const prev = this.toolStack.pop();
    if (prev) this.swapTool(prev);
  }

  private activateModalTool(tool: CanvasTool): void {
    if (this.currentTool === tool) return;
    this.toolStack.push(this.currentTool);
    this.swapTool(tool);
  }

  private runUndo(): void {
    const circuit = currentCircuit.value;
    if (!circuit) return;
    const reverted = this.undoStack.undo(circuit);
    if (reverted) currentCircuit.value = reverted;
  }

  private runRedo(): void {
    const circuit = currentCircuit.value;
    if (!circuit) return;
    const replayed = this.undoStack.redo(circuit);
    if (replayed) currentCircuit.value = replayed;
  }

  private async runCopy(): Promise<void> {
    const circuit = currentCircuit.value;
    if (!circuit) return;
    const ids = this.selectedNodes.value;
    const fragment = fragmentFromSelection(circuit, ids);
    if (!fragment) return;
    try {
      await copyToClipboard(fragment);
    } catch (err) {
      console.warn("[vxa-canvas] copy failed", err);
    }
  }

  private async runPaste(): Promise<void> {
    const circuit = currentCircuit.value;
    if (!circuit) return;
    const payload = await readFromClipboard();
    if (!payload) return;
    const cursor = this.cursorWorld();
    currentCircuit.value = this.undoStack.execute(new PasteCommand(payload, cursor), circuit);
  }

  private async runCut(): Promise<void> {
    const circuit = currentCircuit.value;
    if (!circuit) return;
    const ids = this.selectedNodes.value;
    if (ids.length === 0) return;
    const fragment = fragmentFromSelection(circuit, ids);
    if (!fragment) return;
    try {
      await copyToClipboard(fragment);
    } catch (err) {
      console.warn("[vxa-canvas] cut failed at copy step", err);
      return;
    }
    this.deleteSelected();
  }

  private deleteSelected(): void {
    const circuit = currentCircuit.value;
    if (!circuit) return;
    const ids = this.selectedNodes.value;
    if (ids.length === 0) return;
    const targets: { kind: "component" | "wire"; id: string }[] = [];
    for (const id of ids) {
      if (circuit.components.some((c) => c.id === id)) targets.push({ kind: "component", id });
      else if (circuit.wires.some((w) => w.id === id)) targets.push({ kind: "wire", id });
    }
    if (targets.length === 0) return;
    currentCircuit.value = this.undoStack.execute(new DeleteCommand(targets, circuit), circuit);
    this.selectedNodes.value = [];
  }

  /**
   * Sprint 18 Paket C C3 — rotate every selected component 90° CW.
   * Multi-select rotations land as a single BatchCommand so a
   * single Cmd+Z reverts the whole rotation. Wires are skipped
   * (rotation is a component-only field).
   */
  private rotateSelected(): void {
    const circuit = currentCircuit.value;
    if (!circuit) return;
    const ids = this.selectedNodes.value;
    if (ids.length === 0) return;
    const commands: Command[] = [];
    for (const id of ids) {
      const cmp = circuit.components.find((c) => c.id === id);
      if (!cmp) continue;
      const next = nextRotation(cmp.rotation);
      commands.push(new RotateCommand(cmp.id, cmp.rotation, next));
    }
    if (commands.length === 0) return;
    const cmd =
      commands.length === 1 && commands[0]
        ? commands[0]
        : new BatchCommand(commands, `${String(commands.length)} bileşen döndürüldü`);
    currentCircuit.value = this.undoStack.execute(cmd, circuit);
  }

  private cursorWorld(): { x: number; y: number } {
    if (!this.lastPointerScreen) return { x: 0, y: 0 };
    const rect = this.canvas.getBoundingClientRect();
    const local = {
      x: this.lastPointerScreen.x - rect.left,
      y: this.lastPointerScreen.y - rect.top,
    };
    return this.viewport.screenToWorld(local, this.canvas);
  }

  private toolContext(): ToolContext {
    return {
      viewport: this.viewport,
      hitTester: this.hitTester,
      canvas: this.canvas,
      hoveredNode: this.hoveredNode,
      selectedNodes: this.selectedNodes,
      circuit: currentCircuit,
      undoStack: this.undoStack,
      worldFromEvent: (e) => {
        const rect = this.canvas.getBoundingClientRect();
        const local = { x: e.clientX - rect.left, y: e.clientY - rect.top };
        return this.viewport.screenToWorld(local, this.canvas);
      },
      invalidate: () => {
        this.dirty.markAllDirty();
        this.renderer?.invalidate();
      },
      // Sprint 58 — bridge the canvas-tool snap-glow sink into the
      // polish overlay store. Tools call `set()` while a snap
      // candidate is in range; the overlay renders a radial glow
      // at the supplied world point (mapped to client coords here
      // so the overlay can position with `position: fixed`).
      snapGlow: {
        set: (target) => {
          const screen = this.viewport.worldToScreen(target.point, this.canvas);
          const rect = this.canvas.getBoundingClientRect();
          setSnapGlowTarget({
            point: { x: screen.x + rect.left, y: screen.y + rect.top },
            intensity: target.intensity,
          });
        },
        clear: () => {
          setSnapGlowTarget(null);
        },
      },
    };
  }
}

/**
 * Inject overlay scene nodes into the existing scene's
 * `selection` layer (zIndex 7). Pure helper so the effect that
 * derives the scene stays declarative.
 */
function injectOverlay(scene: Scene, overlay: readonly SceneNode[]): Scene {
  if (overlay.length === 0) return scene;
  const layers = scene.layers.map((layer) =>
    layer.id === "selection"
      ? ({
          ...layer,
          nodes: [...layer.nodes, ...overlay],
        } satisfies Layer)
      : layer,
  );
  return { ...scene, layers };
}

declare global {
  interface HTMLElementTagNameMap {
    "vxa-canvas": VxaCanvas;
  }
}
